import { useState, type CSSProperties } from 'react'
import { getAvatarColor, getInitials } from '../../utils/avatar-color'

interface AvatarProps {
  name: string
  /** URL da foto de perfil (Google ou Microsoft). Faz fallback para iniciais coloridas. */
  picture?: string | null
  /** Tamanho em pixels (lado do quadrado). Default 28 (h-7 w-7). */
  size?: number
  /** Classe extra (ex.: ring para destaque). */
  className?: string
  title?: string
  /**
   * `solid` (padrão): iniciais brancas sobre a cor. `soft`: iniciais na cor,
   * sobre um fundo claro dela — o estilo das tabelas de Ocorrências/HUB.
   */
  variant?: 'solid' | 'soft'
}

/**
 * Avatar com foto do Google/Microsoft + fallback para iniciais coloridas
 * (deterministic por nome). Mesmo padrão visual usado no DetailsHeader principal.
 *
 * Por que normalizar a URL aqui: o backend devolve `google_picture` como
 * `https://lh3.googleusercontent.com/...` que rejeita hot-link sem `sz=`. Ajustar
 * uma vez aqui evita repetir em cada call site.
 */
export function Avatar({
  name,
  picture,
  size = 28,
  className = '',
  title,
  variant = 'solid',
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false)
  const showImage = !!picture && !imgFailed

  const normalizedSrc = (() => {
    if (!picture) return undefined
    if (picture.includes('googleusercontent.com')) {
      const baseUrl = picture.split('?')[0]
      return `${baseUrl}?sz=${Math.max(64, size * 2)}&d=404`
    }
    return picture
  })()

  if (showImage && normalizedSrc) {
    return (
      <img
        src={normalizedSrc}
        alt={name}
        title={title ?? name}
        onError={() => setImgFailed(true)}
        style={{ width: size, height: size }}
        className={`shrink-0 rounded-full object-cover ${className}`}
      />
    )
  }

  const initials = getInitials(name)
  const color = getAvatarColor(name)
  // <span>: o avatar vive dentro de botões (cards, seletores) — <div> ali não
  // é HTML válido. `soft`: fundo claro OPACO (misturado com a superfície, não
  // transparente — avatares sobrepostos não "vazam") e iniciais escuras; no
  // modo escuro, iniciais claras para ter contraste.
  return (
    <span
      title={title ?? name}
      style={
        {
          width: size,
          height: size,
          fontSize: Math.max(9, Math.round(size * 0.34)),
          '--av': color,
          ...(variant === 'soft' ? {} : { backgroundColor: color }),
        } as CSSProperties
      }
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${
        variant === 'soft'
          ? 'bg-[color-mix(in_srgb,var(--av)_16%,rgb(var(--surface)))] text-[color-mix(in_srgb,var(--av)_80%,black)] dark:bg-[color-mix(in_srgb,var(--av)_28%,rgb(var(--surface)))] dark:text-[color-mix(in_srgb,var(--av)_55%,white)]'
          : 'text-white'
      } ${className}`}
      aria-hidden
    >
      {initials}
    </span>
  )
}
